import { addSelectedSystem } from 'features/systems';
import axios from 'utils/api';
import {
  CompatibilityPositionPlan,
  planCompatibilityPositions
} from 'utils/compatibility-positions';
import {
  resolveCombinationProducts,
  toCombinationPayload
} from 'utils/compatibility-quotable-products';
import type {
  CompatibilityBundleQuotableSystemResponse,
  CompatibilityConfigurationResponse,
  SavedQuotableSystemRecord
} from 'utils/compatibility-quotable-types';
import {
  assembleCompatibilitySelectedSystem,
  buildCompatibilityCondenser,
  CompatibilityCondenserBuild
} from 'utils/compatibility-systems';
import type {
  AddDuctedBundleParams,
  DuctedBundlePairInput
} from 'utils/ducted-bundle-add-types';
import { mergeDuctedCoverage } from 'utils/ducted-coverage';
import { resolveOpenSlotPlaceholderLabels } from 'utils/open-slot-placeholder-labels';
import { errorModal } from 'utils/ui';

type PlannedPair = {
  pair: DuctedBundlePairInput;
  plan: CompatibilityPositionPlan;
};

type ConfiguredPair = PlannedPair & {
  configuration: CompatibilityConfigurationResponse;
};

function planPairs(pairs: DuctedBundlePairInput[]): PlannedPair[] | null {
  const planned = pairs.map((pair) => ({
    pair,
    plan: planCompatibilityPositions(pair.components, [pair.combo])
  }));
  const unmapped = planned.find(({ plan }) => plan.positions.length === 0);
  if (unmapped) {
    errorModal(
      `Cannot add systems: no recognized outdoor/indoor/blower components on ${unmapped.pair.designPair.condenserName}'s existing system.`
    );
    return null;
  }
  return planned;
}

async function createPairConfiguration(
  planned: PlannedPair,
  instanceUniqueId: string
): Promise<ConfiguredPair> {
  const response = await axios.post(
    '/compatibility-quotable-systems/configuration',
    {
      instanceUniqueId,
      systemTypeLabel: planned.pair.combo.systemTypeLabel,
      positions: planned.plan.positions,
      keptComponentLabels: planned.plan.keptComponentLabels
    }
  );
  return {
    ...planned,
    configuration: response.data as CompatibilityConfigurationResponse
  };
}

function toBundlePairPayload({ pair, configuration }: ConfiguredPair) {
  return {
    configurationUniqueId: configuration.configurationUniqueId,
    combination: toCombinationPayload(pair.combo),
    equipmentRecords: configuration.equipmentRecords,
    tonnageSeed: pair.tonnageSeed,
    calculatedLoads: {
      totalCoolingBTU: pair.loads.cooling,
      totalHeatingBTU: pair.loads.heating
    }
  };
}

function buildPairCondenser(
  { pair, plan, configuration }: ConfiguredPair,
  saved: SavedQuotableSystemRecord,
  sizingBands: AddDuctedBundleParams['sizingBands']
): CompatibilityCondenserBuild {
  const { combo } = pair;
  const products = resolveCombinationProducts(combo, saved);
  const openSlotLabels = resolveOpenSlotPlaceholderLabels({
    combo,
    systemTypeLabel: combo.systemTypeLabel,
    keptSlots: plan.keptSlots,
    keptBlower: plan.keptBlower
  });
  return buildCompatibilityCondenser({
    configurationUniqueId: configuration.configurationUniqueId,
    configurationLabel: configuration.label,
    systemTypeLabel: combo.systemTypeLabel,
    combinationPerformance: saved.combinationPerformance,
    ...products,
    ...openSlotLabels,
    keptPairing: combo.keptPairing,
    displayName: combo.displayName,
    ductedCoverage: mergeDuctedCoverage(
      saved.ductedCoverage,
      combo.designCoverage
    ),
    operatingCost: combo.operatingCost ?? null,
    binCoverage: combo.binCoverage ?? null,
    sizingBands,
    existingSystemUniqueId: pair.systemUniqueId,
    compatibilityCombinationId: combo.combinationId,
    designPair: pair.designPair,
    cardId: pair.cardId
  });
}

export async function addDuctedBundleToQuotable({
  pairs,
  instanceUniqueId,
  sizingBands,
  dispatch,
  onSuccess
}: AddDuctedBundleParams): Promise<void> {
  if (pairs.length === 0) return;
  if (!instanceUniqueId) {
    errorModal('Cannot add systems: project instance not loaded.');
    return;
  }
  const planned = planPairs(pairs);
  if (!planned) return;

  try {
    const configured = await Promise.all(
      planned.map((entry) => createPairConfiguration(entry, instanceUniqueId))
    );
    const response = await axios.post(
      '/compatibility-quotable-systems/bundle',
      { pairs: configured.map(toBundlePairPayload) }
    );
    const { quotableSystem, confidence } =
      response.data as CompatibilityBundleQuotableSystemResponse;
    if (quotableSystem.pairs.length !== configured.length) {
      throw new Error('The server saved a different number of systems.');
    }

    const selectedSystem = assembleCompatibilitySelectedSystem({
      quotableSystemUniqueId: quotableSystem.uniqueId,
      pairs: configured.map((entry, index) =>
        buildPairCondenser(entry, quotableSystem.pairs[index], sizingBands)
      ),
      confidence: confidence ?? null,
      designConfigurationUniqueId:
        pairs[0].designPair.designConfigurationUniqueId
    });

    dispatch(addSelectedSystem(selectedSystem));
    onSuccess([selectedSystem]);
  } catch (error) {
    console.error('Failed to add the ducted system bundle:', error);
    errorModal(
      error instanceof Error
        ? `Failed to add systems: ${error.message}`
        : 'Failed to add systems to quotable. Please try again.'
    );
  }
}
